/**
 * Formatters utility
 * Centralized formatting functions for prices, dates, text, etc.
 */

// Price formatting (without currency symbol)
export const formatPrice = (price: number): string => {
  return price.toLocaleString('es-AR');
};

// Currency formatting
export const formatCurrency = (price: number): string => {
  return `$ ${formatPrice(price)}`;
};

// Number formatting
export const formatNumber = (num: number): string => {
  return num.toLocaleString('es-AR');
};

// Percent formatting (no decimals)
export const formatPercent = (value: number): string => {
  return `${Math.round(value * 100)}%`;
};

// Bytes formatting
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

// Duration formatting (HH:MM:SS or MM:SS)
export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Date formatting (DD/MM/YYYY)
export const formatDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// DateTime formatting
export const formatDateTime = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Relative time formatting
export const formatRelativeTime = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return 'hace un momento';
  if (diffMins < 60) return `hace ${diffMins} min`;
  
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `hace ${diffHours} horas`;
  
  const diffDays = Math.floor(diffHours / 24);
  return `hace ${diffDays} días`;
};

// Compact relative time formatting (for chats, notifications, etc.)
export const formatRelativeTimeCompact = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Ahora';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return d.toLocaleDateString('es-AR');
};

// Compact relative time formatting with full text (for questions, reviews, etc.)
export const formatRelativeTimeFull = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'justo ahora';
  if (diffMins < 60) return `hace ${diffMins} min`;
  if (diffHours < 24) return `hace ${diffHours} hr${diffHours > 1 ? 's' : ''}`;
  if (diffDays < 7) return `hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
  return d.toLocaleDateString('es-AR');
};

// Text truncation
// TODO: Review unused - only used in tests
export const truncateText = (text: string, maxLength: number, suffix = '...'): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + suffix;
};

// Capitalize first letter
// TODO: Review unused - only used in tests
export const capitalizeFirst = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Payment formatters
export const paymentFormatters = {
  formatCardNumber: (number: string): string => {
    return number.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  },
  
  formatExpiry: (expiry: string): string => {
    return expiry.replace(/(\d{2})(\d{2})/, '$1/$2');
  }
};

// Payment validators
export const paymentValidators = {
  isValidCardNumber: (number: string): boolean => {
    const clean = number.replace(/\s/g, '');
    return clean.length === 16 && /^\d+$/.test(clean);
  },
  
  isValidCVV: (cvv: string): boolean => {
    return /^\d{3}$/.test(cvv);
  },
  
  isValidName: (name: string): boolean => {
    return name.trim().length >= 2;
  }
};

// Auth error formatter
export const formatAuthError = (error: { code?: string }): string => {
  const errorMessages: Record<string, string> = {
    'auth/user-not-found': 'Usuario no encontrado.',
    'auth/wrong-password': 'Contraseña incorrecta.',
    'auth/email-already-in-use': 'El email ya está registrado.',
    'auth/weak-password': 'La contraseña es muy débil.',
    'auth/invalid-email': 'Email inválido.',
    'auth/too-many-requests': 'Demasiados intentos. Intenta más tarde.'
  };
  
  return errorMessages[error.code || ''] || 'Ocurrió un error inesperado.';
};